/**
 * REGISTA AI: D1 API Client
 * 
 * API client for D1-powered backend.
 * Handles authentication, org context, and API requests.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';

// Configuration
const API_CONFIG = {
  apiUrl: import.meta.env.VITE_API_URL || ''  // Use same domain for Pages Functions
};

interface User {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
  emailVerified?: boolean;
  createdAt?: string;
  memberships?: Membership[];
}

interface Membership {
  role: string;
  squad_id: string;
  squad_name: string;
  org_id: string;
  org_name: string;
  org_slug: string;
}

interface ApiContext {
  user: User | null;
  currentOrgId: string | null;
  authToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Methods
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setCurrentOrg: (orgId: string) => void;
  apiRequest: <T = any>(endpoint: string, options?: RequestInit) => Promise<T>;
}

const ApiContext = createContext<ApiContext | null>(null);

interface ApiProviderProps {
  children: React.ReactNode;
}

export function ApiProvider({ children }: ApiProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize authentication state
  useEffect(() => {
    initializeAuth();
  }, []);

  async function initializeAuth() {
    try {
      setIsLoading(true);
      
      // Check for stored auth token
      const storedToken = localStorage.getItem('registaai:authToken');
      if (storedToken) {
        setAuthToken(storedToken);
        await fetchUserProfile(storedToken);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Authentication failed');
      // Clear invalid token
      localStorage.removeItem('registaai:authToken');
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchUserProfile(token: string) {
    try {
      const response = await fetch(`${API_CONFIG.apiUrl}/api/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Org-ID': currentOrgId || '', // Will be handled by API if empty
        }
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const userData = await response.json();
      setUser(userData);

      // Set default org if available
      if (userData.memberships && userData.memberships.length > 0 && !currentOrgId) {
        setCurrentOrgId(userData.memberships[0].org_id);
      }
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
      throw err;
    }
  }

  async function login(email: string, password: string) {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${API_CONFIG.apiUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Login failed');
      }

      const { token, user } = await response.json();
      setAuthToken(token);
      setUser(user);
      
      // Store token in localStorage
      localStorage.setItem('registaai:authToken', token);
      
      // Set default org if available
      if (user.memberships && user.memberships.length > 0) {
        setCurrentOrgId(user.memberships[0].org_id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }

  async function logout() {
    try {
      // Call logout endpoint if needed
      if (authToken) {
        await fetch(`${API_CONFIG.apiUrl}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
          }
        });
      }
    } catch (err) {
      console.warn('Logout API call failed:', err);
    } finally {
      // Clear local state regardless of API call success
      setUser(null);
      setAuthToken(null);
      setCurrentOrgId(null);
      setError(null);
      localStorage.removeItem('registaai:authToken');
    }
  }

  function setCurrentOrg(orgId: string) {
    setCurrentOrgId(orgId);
    localStorage.setItem('registaai:currentOrgId', orgId);
  }

  async function apiRequest<T = any>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>
    };

    // Add authentication
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    // Add org context
    if (currentOrgId) {
      headers['X-Org-ID'] = currentOrgId;
    }

    try {
      const response = await fetch(`${API_CONFIG.apiUrl}${endpoint}`, {
        ...options,
        headers
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage: string;
        
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || `HTTP ${response.status}`;
        } catch {
          errorMessage = errorText || `HTTP ${response.status}`;
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      throw err;
    }
  }

  const contextValue: ApiContext = {
    user,
    currentOrgId,
    authToken,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    logout,
    setCurrentOrg,
    apiRequest
  };

  return (
    <ApiContext.Provider value={contextValue}>
      {children}
    </ApiContext.Provider>
  );
}

export function useApi(): ApiContext {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context;
}

// Convenience hook for authenticated API requests
export function useApiRequest() {
  const { apiRequest, isAuthenticated } = useApi();
  
  return {
    request: apiRequest,
    isAuthenticated
  };
}

// Hook for org management
export function useOrganization() {
  const { user, currentOrgId, setCurrentOrg } = useApi();
  
  const currentOrg = user?.memberships?.find(m => m.org_id === currentOrgId);
  const availableOrgs = user?.memberships?.map(m => ({
    id: m.org_id,
    name: m.org_name,
    slug: m.org_slug,
    role: m.role
  })) || [];

  return {
    currentOrg,
    currentOrgId,
    availableOrgs,
    setCurrentOrg,
    userRole: currentOrg?.role
  };
}